import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useProgramSessionWeekDuplicate } from "@/modules/sessions/hooks/useProgramSessionWeekDuplicate"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { ProgramType } from "@modules/programs/types/programTypes"
import { useProgramSessionEvents } from "@modules/training/hooks/useTraining"
import dayjs from "dayjs"
import { useImperativeHandle, useState } from "react"

export type ProgramWeekCopyModalRef = {
  onPasteWeekClick: () => Promise<void>
} & React.ComponentProps<"div">

export type ProgramWeekCopyModalProps = {
  program: UserProgramDetailsEntity
  calendarRange:
    | { startDate: number | undefined; endDate: number | undefined }
    | { startDate: string; endDate: string }
    | undefined
  ref: React.Ref<ProgramWeekCopyModalRef>
  className?: string
}

export const ProgramWeekCopyModal = ({
  program,
  calendarRange,
  ref,
  className,
  ...props
}: ProgramWeekCopyModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const { refreshCalendarEvents, selectedDate } = useCalendar()
  const {
    data: { sessions: programSessions },
  } = useProgramSessionEvents({
    programId: program.id,
    programType: [ProgramType.VIRTUAL, ProgramType.PRO, ProgramType.ONOFF].includes(program.type)
      ? (program.type as ProgramType.VIRTUAL | ProgramType.PRO | ProgramType.ONOFF | undefined)
      : undefined,
    startDate: calendarRange?.startDate,
    endDate: calendarRange?.endDate,
  })
  const { onPasteInfiniteProgramWeek, onPasteOnOffProgramWeek, onPasteProProgramWeek, weekToCopy } =
    useProgramSessionWeekDuplicate()

  const currentWeekHasSessions = programSessions && programSessions.length > 0
  const copiedSessionsAreFromDifferentProgram = weekToCopy?.originProgramId !== program.id

  const title = currentWeekHasSessions
    ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.title")
    : copiedSessionsAreFromDifferentProgram
      ? word("program.weekCopyPaste.paste.modale.pasteFromAnotherProgram.title", {
          originProgramName: weekToCopy?.originProgramName,
        })
      : ""
  const message =
    copiedSessionsAreFromDifferentProgram && currentWeekHasSessions
      ? word("program.weekCopyPaste.paste.modale.pasteFromAnotherProgramAndSessionsAlreadyExist.message", {
          originProgramName: weekToCopy?.originProgramName,
        })
      : currentWeekHasSessions
        ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.message")
        : ""

  const onHide = () => {
    setShowModal(false)
  }

  const onPasteWeek = async (overwrite?: boolean) => {
    setIsSubmitting(true)
    if (program.type === ProgramType.VIRTUAL && !!selectedDate && typeof selectedDate !== "number") {
      await onPasteInfiniteProgramWeek(program.id, dayjs(selectedDate).weekday(0).toISOString(), overwrite)
    } else if (program.type === ProgramType.ONOFF && typeof selectedDate === "number") {
      await onPasteOnOffProgramWeek(program.id, selectedDate, overwrite)
    } else if (program.type === ProgramType.PRO && !!selectedDate && typeof selectedDate !== "number") {
      await onPasteProProgramWeek(program.id, dayjs(selectedDate).weekday(0).toISOString(), overwrite)
    }
    setIsSubmitting(false)
    setShowModal(false)
    refreshCalendarEvents()
  }

  useImperativeHandle(ref, () => ({
    onPasteWeekClick: async () => {
      if (currentWeekHasSessions || copiedSessionsAreFromDifferentProgram) {
        setShowModal(true)
      } else {
        await onPasteWeek()
      }
    },
  }))

  const onLeftButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (currentWeekHasSessions) {
      await onPasteWeek(true)
    }
    setShowModal(false)
  }

  const onRightButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    await onPasteWeek(false)
  }

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className={cn("sm:max-w-md", className)} {...props}>
        <DialogHeader>
          <DialogTitle>
            {/* HEADER */}
            <Text fontStyle="TL" className="text-center">
              {title}
            </Text>
          </DialogTitle>
        </DialogHeader>

        <DialogDescription>
          {/* CONTENT */}
          <Text fontStyle="BM" className="text-center wrap-break-word whitespace-break-spaces text-textDefault-caption">
            {message}
          </Text>
        </DialogDescription>

        <DialogFooter>
          {/* BUTTON */}
          <div className="mb-3 flex flex-row items-center justify-center gap-4">
            <Button variant="outline" onClick={(event) => void onLeftButtonClick(event)}>
              {currentWeekHasSessions
                ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.replace")
                : word("global.cancel")}
            </Button>

            <Button variant="primary" onClick={(event) => void onRightButtonClick(event)} disabled={isSubmitting}>
              {currentWeekHasSessions
                ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.keep")
                : word("global.paste")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
